import { DateTimeText } from "@nexus/ui";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CalendarClock, Check, Filter, LayoutList, TriangleAlert } from "lucide-react";
import {
  firstError,
  resolveLabels,
  unwrap,
  Widget,
  WidgetCollapseContext,
  WidgetState,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type {
  DrawerCallbacks,
  DrawerDescriptor,
  JsonValue,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import type {
  EventTimelineConfig,
  EventTimelineData,
  TimelineEvent,
  TimelineViewMode,
} from "./types";
import {
  eventTenantLabel,
  formatDuration,
  formatRelative,
  HOUR_MS,
  isOngoing,
  LiveDot,
  orderChannels,
  parseDbTime,
  TIMELINE_LABELS as LABELS,
  TimelineView,
  titleCase,
  useNow,
} from "@nexus/widget-kit";
import { EventListView } from "./EventListView";

export type {
  EventTimelineConfig,
  EventTimelineData,
  TimelineEvent,
  TimelineProtectedEvent,
} from "./types";

export { TIMELINE_LABELS as LABELS } from "@nexus/widget-kit";

export const manifest: WidgetManifest = {
  id: "event-timeline",
  name: "Event Timeline",
  description:
    "Temporal context for the dashboard: what is running now, where the anomalies are, and what the dashboard is scoped to.",
  version: "4.2.0",
  dataRequests: {
    events: {
      source: "event-timeline",
      contract: { name: "event-timeline", version: 1 },
      configurable: false,
    },
  },
  labels: LABELS,
};

export const defaultConfig: EventTimelineConfig = {
  title: "Event Timeline",
  interactionType: "event.scope",
  defaultCollapsed: false,
  defaultSpan: 3 * HOUR_MS,
  timeLabelMode: "absolute",
  refreshIntervalMs: 30_000,
};

const EMPTY_EVENTS: TimelineEvent[] = [];

function EventTimelineComponent({ data, config, dashboard }: WidgetProps<EventTimelineConfig>) {
  const error = firstError(data, ["events"]);
  const resolved = unwrap<EventTimelineData>(data.events);
  const isLoading = data.events?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Event Timeline";
  const labels = resolveLabels(LABELS, config);
  const interactive = typeof dashboard.emitInteraction === "function";

  const events = resolved?.events ?? EMPTY_EVENTS;
  const nowMs = useNow(config.refreshIntervalMs ?? defaultConfig.refreshIntervalMs);

  const [view, setView] = useState<TimelineViewMode>("timeline");

  const [spanMs, setSpanMs] = useState<number>(
    config.defaultSpan ?? defaultConfig.defaultSpan ?? 3 * HOUR_MS,
  );
  const timeLabelMode = config.timeLabelMode ?? defaultConfig.timeLabelMode ?? "absolute";
  const [followNow, setFollowNow] = useState(true);
  const [manualCenter, setManualCenter] = useState(nowMs);

  const centerMs = followNow ? nowMs : manualCenter;
  const centerRef = useRef(centerMs);
  centerRef.current = centerMs;

  const handleCenterChange = useCallback((updater: (center: number) => number) => {
    const next = updater(centerRef.current);
    centerRef.current = next;
    setFollowNow(false);
    setManualCenter(next);
  }, []);

  const centerOnNow = useCallback(() => setFollowNow(true), []);

  const scopedEventId = dashboard.filters.dataFilters.find((filter) => filter.field === "event_id")
    ?.value as string | undefined;

  const handleSelectEvent = useCallback(
    (event: TimelineEvent) => {
      dashboard.emitInteraction?.({
        type: "event.detail",
        payload: {
          event: event as unknown as JsonValue,
          scopedEventId: scopedEventId ?? null,
        } as JsonValue,
        source: { dataKey: "events" },
      });
    },
    [dashboard, scopedEventId],
  );

  const anomalyCount = useMemo(
    () => events.reduce((total, event) => total + event.unresolved_anomaly_count, 0),
    [events],
  );

  const isEmpty = events.length === 0;

  const { collapsed } = useContext(WidgetCollapseContext);

  const actions = (
    <div className="flex items-center gap-2">
      {anomalyCount > 0 && (
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold tabular-nums text-status-error-text"
          title={`${anomalyCount} unresolved ${anomalyCount === 1 ? "anomaly" : "anomalies"}`}
        >
          <TriangleAlert className="h-3.5 w-3.5" aria-hidden />
          {anomalyCount}
        </span>
      )}

      {!collapsed && !isEmpty && <span className="h-5 w-px bg-border" aria-hidden />}

      {!collapsed && !isEmpty && (
        <PillGroup>
          <PillButton
            active={view === "timeline"}
            onClick={() => setView("timeline")}
            label={labels.timelineView}
          >
            <CalendarClock className="h-4 w-4" aria-hidden />
          </PillButton>
          <PillButton
            active={view === "list"}
            onClick={() => setView("list")}
            label={labels.listView}
          >
            <LayoutList className="h-4 w-4" aria-hidden />
          </PillButton>
        </PillGroup>
      )}
    </div>
  );

  if (error) {
    console.error("[EventTimelineWidget] Failed to load data:", error);
    return (
      <Widget title={title} actions={actions}>
        <WidgetIssueState error={error} className="py-5" />
      </Widget>
    );
  }

  if (isEmpty) {
    return (
      <Widget title={title} actions={actions}>
        {isLoading ? (
          <WidgetSkeleton shape="timeline" />
        ) : (
          <WidgetState
            title={labels.emptyTitle}
            description={labels.emptyDescription}
            className="py-5"
          />
        )}
      </Widget>
    );
  }

  return (
    <Widget title={title} actions={actions}>
      <div className="@container flex h-full min-h-0 flex-col px-4 pt-1 pb-4">
        {view === "timeline" ? (
          <TimelineView
            events={events}
            nowMs={nowMs}
            spanMs={spanMs}
            labelMode={timeLabelMode}
            centerMs={centerMs}
            onCenterChange={handleCenterChange}
            onSpanChange={setSpanMs}
            onJumpToNow={centerOnNow}
            selectedEventId={scopedEventId}
            interactive={interactive}
            onSelectEvent={handleSelectEvent}
            labels={labels}
          />
        ) : (
          <EventListView
            events={events}
            nowMs={nowMs}
            selectedEventId={scopedEventId}
            interactive={interactive}
            onSelectEvent={handleSelectEvent}
            labels={labels}
          />
        )}
      </div>
    </Widget>
  );
}

// ── Detail drawer rendering ──────────────────────────────────────────────────

export function renderDetail(
  payload: JsonValue,
  callbacks: DrawerCallbacks,
): DrawerDescriptor | null {
  const { event, scopedEventId } = payload as unknown as {
    event: TimelineEvent;
    scopedEventId?: string;
  };
  if (!event?.event_id) return null;

  const nowMs = Date.now();
  const start = parseDbTime(event.event_start);
  const end = parseDbTime(event.event_end);
  const live = isOngoing(event, nowMs);
  const problem = event.unresolved_anomaly_count > 0;
  const channels = orderChannels(event.protected_events);
  const tenant = eventTenantLabel(event);
  const isFiltered = scopedEventId === event.event_id;

  const statusText = live ? "Live now" : start <= nowMs ? "Ended" : "Upcoming";
  const statusSub = live ? (
    `Ends ${formatRelative(nowMs, end)}`
  ) : start <= nowMs ? (
    <DateTimeText value={end} options={{ weekday: "short", month: "short", day: "numeric" }} />
  ) : (
    `Starts ${formatRelative(nowMs, start)}`
  );

  return {
    title: event.event_label,
    subtitle: (
      <>
        {titleCase(event.event_type || "Event")} ·{" "}
        <DateTimeText
          value={start}
          options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
        />
        –
        <DateTimeText
          value={end}
          options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
        />{" "}
        · {formatDuration(start, end)}
      </>
    ),
    meta: (
      // Shown in the detail drawer, outside the widget: its own width decides.
      <div className="@container">
        <div className="grid gap-3 @lg:grid-cols-2 @4xl:grid-cols-4">
          <div className="rounded-lg border border-border bg-surface-elevated p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Status</p>
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary">
              {live && <LiveDot tone={problem ? "error" : "accent"} />}
              {statusText}
            </p>
            <p className="mt-0.5 text-xs text-muted">{statusSub}</p>
          </div>

          <div className="rounded-lg border border-border bg-surface-elevated p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Timing</p>
            <p className="mt-1 text-sm font-medium tabular-nums text-primary">
              <DateTimeText
                value={start}
                options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
              />
              –
              <DateTimeText
                value={end}
                options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
              />
            </p>
            <p className="mt-0.5 text-xs text-muted">
              <DateTimeText
                value={start}
                options={{ weekday: "short", month: "short", day: "numeric" }}
              />{" "}
              · {formatDuration(start, end)}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-surface-elevated p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Channels</p>
            <p className="mt-1 text-sm font-medium text-primary">{channels.length}</p>
            {problem && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-status-error-text">
                <TriangleAlert className="h-3 w-3" aria-hidden />
                {event.unresolved_anomaly_count} with anomalies
              </p>
            )}
          </div>

          {tenant ? (
            <div className="rounded-lg border border-border bg-surface-elevated p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Tenant</p>
              <p className="mt-1 text-sm font-medium text-primary">{tenant}</p>
              <p className="mt-0.5 text-xs text-muted">{titleCase(event.event_type || "")}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-surface-elevated p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Type</p>
              <p className="mt-1 text-sm font-medium text-primary">
                {titleCase(event.event_type || "—")}
              </p>
            </div>
          )}
        </div>
      </div>
    ),
    children: (
      <div className="space-y-4">
        {channels.length > 0 ? (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Channels ({channels.length})
            </p>
            <ul className="flex flex-col gap-1">
              {channels.map((pe) => {
                const chProblem = pe.unresolved_anomaly_count > 0;
                return (
                  <li
                    key={pe.protected_event_id}
                    className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-secondary">
                      {pe.channel || "—"}
                    </span>
                    <span className="shrink-0 text-xs text-muted">{pe.tenant_label}</span>
                    {chProblem && (
                      <span className="flex shrink-0 items-center gap-1 text-status-error-text">
                        <TriangleAlert className="h-3 w-3" aria-hidden />
                        <span className="text-xs font-semibold tabular-nums">
                          {pe.unresolved_anomaly_count}
                        </span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted">No channels configured for this event.</p>
        )}
      </div>
    ),
    footer: (
      <button
        type="button"
        onClick={() => {
          if (isFiltered) {
            callbacks.onInteraction("event.scope", { scope: "clear" } as JsonValue);
          } else {
            callbacks.onInteraction("event.scope", {
              scope: "event",
              event_id: event.event_id,
              event_label: event.event_label,
              channel_count: event.protected_events.length,
            } as JsonValue);
          }
          callbacks.onClose();
        }}
        aria-pressed={isFiltered}
        className={[
          "inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/45",
          isFiltered
            ? "border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/[0.16]"
            : "border-border bg-surface text-secondary hover:border-brand-cyan/40 hover:text-primary",
        ].join(" ")}
      >
        {isFiltered ? (
          <>
            <Check className="h-4 w-4" aria-hidden />
            Clear event filter
          </>
        ) : (
          <>
            <Filter className="h-4 w-4" aria-hidden />
            Filter dashboard to this event
          </>
        )}
      </button>
    ),
    defaultWidthVw: 36,
  };
}

// ── UI primitives ────────────────────────────────────────────────────────────

function PillGroup({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex h-7 items-center rounded-lg border border-border bg-surface p-0.5">
      {children}
    </div>
  );
}

function PillButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`inline-flex h-6 min-w-6 items-center justify-center rounded-md px-1 transition-colors ${
        active
          ? "bg-brand-cyan/10 text-brand-cyan"
          : "text-muted hover:bg-surface-elevated hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

export const Component = EventTimelineComponent;

const widgetModule: WidgetModule<EventTimelineConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
